/**
 * PostHog Configuration
 *
 * Controls session recording, feature flags, and analytics.
 *
 * Default behavior:
 * - PRODUCTION: Always enabled
 * - DEVELOPMENT: Disabled by default, opt-in with REACT_APP_POSTHOG_ENABLED=true
 *
 * To enable in local development:
 * 1. Add to .env.local: REACT_APP_POSTHOG_ENABLED=true
 * 2. Restart dev server
 */

import { logger } from '../utils/logger';
import posthog from 'posthog-js';
import { FLAG_DEFAULTS, FeatureFlagKey } from './featureFlags';

// PostHog API key from environment
export const POSTHOG_KEY = import.meta.env.REACT_APP_POSTHOG_KEY || '';
export const POSTHOG_HOST = import.meta.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

/**
 * Check if PostHog should be enabled
 */
export const isPostHogEnabled = (): boolean => {
  // If no API key, don't enable
  if (!POSTHOG_KEY) {
    logger.warn('[PostHog] No API key configured. Analytics disabled.');
    return false;
  }

  const isProduction = import.meta.env.PROD;
  const explicitlyEnabled = import.meta.env.REACT_APP_POSTHOG_ENABLED === 'true';

  // In production: always enabled (if API key exists)
  if (isProduction) {
    return true;
  }

  // In development: only if explicitly enabled
  if (explicitlyEnabled) {
    logger.info('[PostHog] Enabled in development mode (REACT_APP_POSTHOG_ENABLED=true)');
    return true;
  }

  logger.info('[PostHog] Disabled in development. Set REACT_APP_POSTHOG_ENABLED=true to enable.');
  return false;
};

/**
 * PostHog initialization options
 */
export const getPostHogConfig = () => ({
  api_host: POSTHOG_HOST,
  
  // Use memory persistence to avoid cookie issues with cross-domain
  persistence: 'memory' as const,
  
  // Disable cookies entirely for cross-origin support
  disable_cookie: true,
  
  // Cross-origin fetch settings
  fetch_options: {
    mode: 'cors' as RequestMode,
    credentials: 'omit' as RequestCredentials,
  },
  
  // Capture page views automatically
  capture_pageview: true,
  
  // Capture page leaves for session duration
  capture_pageleave: true,
  
  // Session recording settings
  disable_session_recording: false,
  session_recording: {
    // Mask all text inputs by default for privacy
    maskAllInputs: true,
    // But allow unmasking specific inputs with data-ph-capture attribute
    maskInputOptions: {
      password: true,
      email: false,
      text: false,
    },
    // Mask text content in sensitive areas
    maskTextSelector: '[data-ph-mask]',
  },
  
  // Feature flags
  bootstrap: {
    // Feature flags will be loaded on init
    featureFlags: {},
  },
  
  // Privacy settings
  respect_dnt: true, // Respect Do Not Track browser setting
  
  // Network recording - useful for debugging but can be disabled
  enable_recording_console_log: true,
  
  // Autocapture settings
  autocapture: {
    // Capture all clicks, form submissions, etc.
    dom_event_allowlist: ['click', 'submit', 'change'] as any,
    // Don't capture sensitive URLs
    url_ignorelist: [
      'googleapis.com',
      'firebaseapp.com',
      'identitytoolkit.googleapis.com',
    ],
  },
  
  // Don't send during test
  loaded: (posthogInstance: typeof posthog) => {
    // Expose for debugging in dev
    if (import.meta.env.DEV) {
      (window as any).posthog = posthogInstance;
    }
  },
});

/**
 * Initialize PostHog
 * Call this once at app startup
 */
export const initPostHog = (): boolean => {
  if (!isPostHogEnabled()) {
    return false;
  }

  try {
    posthog.init(POSTHOG_KEY, getPostHogConfig() as any);
    logger.info('[PostHog] Analytics initialized');
    return true;
  } catch (error) {
    logger.error('[PostHog] Failed to initialize:', error);
    return false;
  }
};

/**
 * Get the PostHog instance
 * Use this for direct PostHog calls
 */
export const getPostHog = () => posthog;

/**
 * Identify a user for targeting and analytics
 * Call this after login
 */
export const identifyUser = (
  userId: string,
  properties?: {
    email?: string;
    name?: string;
    org_id?: string;
    org_name?: string;
    [key: string]: any;
  }
) => {
  if (!isPostHogEnabled()) return;
  
  posthog.identify(userId, properties);
  
  // Set org as a group for org-level targeting
  if (properties?.org_id) {
    posthog.group('organization', properties.org_id, {
      name: properties.org_name,
    });
  }
  
  logger.info('[PostHog] User identified:', userId);
};

/**
 * Reset user identity on logout
 */
export const resetUser = () => {
  if (!isPostHogEnabled()) return;
  
  posthog.reset();
  logger.info('[PostHog] User reset');
};

/**
 * Track a custom event
 */
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  if (!isPostHogEnabled()) return;
  
  posthog.capture(eventName, properties);
};

/**
 * Check if a feature flag is enabled
 * Automatically uses FLAG_DEFAULTS when PostHog is disabled (local dev)
 */
export const isFeatureEnabled = (flagName: string): boolean | undefined => {
  if (!isPostHogEnabled()) {
    // Use default from FLAG_DEFAULTS, or true if flag not defined (permissive for dev)
    const defaultValue = FLAG_DEFAULTS[flagName as FeatureFlagKey];
    return defaultValue !== undefined ? defaultValue : true;
  }
  
  return posthog.isFeatureEnabled(flagName);
};

/**
 * Get a feature flag value (for multivariate flags)
 */
export const getFeatureFlag = (flagName: string): string | boolean | undefined => {
  if (!isPostHogEnabled()) return undefined;
  
  return posthog.getFeatureFlag(flagName);
};

/**
 * Reload feature flags (useful after identify)
 */
export const reloadFeatureFlags = (): Promise<void> => {
  if (!isPostHogEnabled()) return Promise.resolve();
  
  return new Promise((resolve) => {
    posthog.reloadFeatureFlags();
    // PostHog doesn't return a promise, so we wait a bit
    setTimeout(resolve, 500);
  });
};

